import debug from "debug";
import {
  kagi,
  pointAndFigure,
  renko,
  heikinAshi,
  rangeBars,
} from "@chartLib/lib/indicator";
import renkoChartIQ from "@chartLib/lib/calculator/renkoChartIQ";
import offsetSeries from "@chartLib/lib/calculator/offsetSeries";
import { topologicalSort } from "../../../utils/indicatorDependencyValidator.js";
import { isPhaseEnabled } from "../../../config/featureFlags.js";
import {
  setIndicatorsContext,
  clearIndicatorsContext,
} from "@chartLib/lib/utils/getSourcePath";

const log = debug("odin:calculators");

const calculators = {
  candle: (d) => d,
  bar: (d) => d,
  line: (d) => d,
  colorbar: (d) => d,
  rangebars: (d) => rangeBars().options({ range: 10, sourcePath: "close" })(d),
  heikinashi: (d) => heikinAshi()(d),
  kagi: (d) => kagi()(d),
  // Dois motores em paralelo (#82). DEFAULT = "chartiq" (port fiel do
  // calculateRenkoBars do HtmlCharts5, paridade provada em
  // tools/htmlchart-oracle/PROVA-RENKO.md); "legacy" = motor anterior do
  // WebCharts26, mantido por opção explícita no seletor.
  renko: (d, config) =>
    config?.engine === "legacy"
      ? renko().options(config)(d)
      : renkoChartIQ().options(config)(d),
  pointfigure: (d, config) => pointAndFigure().options(config)(d),
};

// Tipos que AGREGAM as barras (a saída não é 1:1 com a entrada e pode ser
// vazia). Exportado para a UI distinguir "agregação não produziu nada" de
// "ainda carregando" (#432).
export const AGGREGATION_TYPES = [
  "pointfigure",
  "renko",
  "kagi",
  "linebreak",
  "rangebars",
];

const emptyResultWarned = {};

export const runCalculator = (chartType, data, chartTypeConfig) => {
  try {
    const calc = calculators[chartType] || calculators.candle;
    let result;

    // Pass config to calculators that need it
    if (chartType === "renko" && chartTypeConfig) {
      result = calc(data, chartTypeConfig);
    } else if (chartType === "pointfigure" && chartTypeConfig) {
      result = calc(data, chartTypeConfig);
    } else {
      result = calc(data);
    }

    // If calculator returns empty array, DON'T fall back for aggregation types
    // Falling back to OHLCV data causes incompatible format errors
    if (!result || result.length === 0) {
      log("calculator %s returned empty result", chartType);
      // warn-once por tipo: roda a cada quote batch — sem isso, símbolo com
      // resultado vazio (ex.: dado flat p/ P&F) inunda o console por tick
      if (!emptyResultWarned[chartType]) {
        emptyResultWarned[chartType] = true;
        console.warn(
          `[${chartType}] Calculator returned empty result. Input data length: ${data?.length}`,
        );
      }
      // For aggregation types, return empty array - the guard in ChartWrapper will handle it
      if (AGGREGATION_TYPES.includes(chartType)) {
        return [];
      }
      // For regular chart types, fall back to original data
      return data;
    }

    return result;
  } catch (e) {
    log("calculator error for %s: %o", chartType, e);
    console.error(`[${chartType}] Calculator error:`, e);
    // Same logic for errors
    if (AGGREGATION_TYPES.includes(chartType)) {
      return [];
    }
    return data;
  }
};

/**
 * Campos BRUTOS da barra — a entrada de todo calculator. Saída de indicador
 * nunca pode morar aqui: sobrescrever um deles destrói o dado de quem calcula
 * depois, no mesmo passe.
 *
 * #440: o indicador Volume tem `key: "volume"` (volumeRegistry.jsx), igual ao
 * campo da barra. Com Qtd + Neg + Fin no mesmo gráfico, as duas primeiras
 * gravavam {volume, direction} em `d.volume` pelo staticKey e a terceira lia
 * esse objeto no lugar do número — plotava vazio ("n/a"). O mesmo estrago
 * atingia qualquer cálculo que use volume bruto (darvasBox, kagi, rangeBars,
 * pointAndFigure) e explica os desembrulhos espalhados em mfi/klinger/
 * accumDist/chaikinMoneyFlow e no merge de tempo real (chartDataProcessor).
 */
const BAR_FIELDS = new Set([
  "date",
  "open",
  "high",
  "low",
  "close",
  "volume",
  "tickCount",
  "financialVolume",
]);

export const getCalculatedIndicatorData = (indicatorDef) => {
  if (!indicatorDef.type || typeof indicatorDef.type !== "function") {
    throw new Error("indicatorDef must have a 'type' function property.");
  }

  const indicator = indicatorDef.type();
  indicator.options(indicatorDef.config);
  const renderKey = indicatorDef.renderKey;
  const staticKey = indicatorDef.key;
  // Sem renderKey próprio não há para onde escapar: o staticKey é a única
  // chave que a série sabe ler, então a gravação continua (comportamento
  // preservado para desktops antigos, onde a colisão já existia).
  const skipStaticKey =
    Boolean(renderKey) && renderKey !== staticKey && BAR_FIELDS.has(staticKey);
  indicator
    .merge((d, c) => {
      if (renderKey) d[renderKey] = c;
      if (staticKey && !skipStaticKey) d[staticKey] = c;
      return d;
    })
    .accessor((d) => (renderKey ? d[renderKey] : d[staticKey]));
  if (typeof indicator.skipUndefined === "function")
    indicator.skipUndefined(true);

  return indicator;
};

/**
 * Deslocamento no tempo, GENÉRICO — o `DESLOCAR` do engine Silverlight / `Offset`
 * do ChartIQ, aplicado a QUALQUER indicador cujo config traga `offsetBars`.
 *
 * Este é o único ponto por onde todo indicador passa, e `getCalculatedIndicatorData`
 * garante que a saída de cada um mora em exatamente uma chave (`renderKey` e/ou
 * `key`) — por isso o deslocamento cabe aqui uma vez, em vez de ser reimplementado
 * calculator a calculator (era assim que existiam 3 cópias divergentes).
 *
 * Deslocar a SAÍDA equivale a deslocar a ENTRADA (o `REF(fonte, N)` do engine) para
 * qualquer cálculo causal e invariante a deslocamento — que é o caso de toda média.
 * Não vale para cálculos ancorados em índice/sessão (VWAP, pivotPoints, LR de janela
 * estática); por isso só quem tinha o parâmetro no legado o expõe na UI.
 *
 * Custo zero no caminho default: `offsetBars` ausente ou 0 sai na primeira linha.
 * Muta apenas os clones criados por `applyIndicators` (I6 preservado).
 */
const applyOffsetBars = (data, indicatorDef) => {
  const bars = Math.trunc(indicatorDef.config?.offsetBars) || 0;
  if (bars === 0) return data;

  const keys = [indicatorDef.renderKey, indicatorDef.key].filter(
    (k, i, arr) => k && arr.indexOf(k) === i,
  );

  for (const key of keys) {
    const shifted = offsetSeries(
      data.map((d) => d[key]),
      bars,
    );
    for (let i = 0; i < data.length; i++) data[i][key] = shifted[i];
  }

  return data;
};

export const applyIndicators = (data, indicators) => {
  if (!data || data.length === 0) return data;
  if (!indicators || indicators.length === 0) return data;

  // Shallow-clone each item so indicator writes (d[staticKey] = c) don't mutate
  // the original objects in the Zustand store. Without this, switching a volume
  // indicator's volumePath causes stale contamination: e.g. d.volume gets
  // overwritten with a processed { volume, direction } object from a previous
  // run, so the next run reads the wrong source value.
  let processedData = data.map((d) => ({ ...d }));

  // PHASE 3: Dependency-ordered processing
  // When CALCULATION phase is enabled, sort indicators by dependencies
  // This ensures indicators calculate in correct order (dependencies first)
  const calculationEnabled = isPhaseEnabled("SERIES_AS_SOURCE", "CALCULATION");
  let indicatorsToProcess = indicators;

  if (calculationEnabled) {
    try {
      // Sort indicators by dependencies using Kahn's algorithm
      indicatorsToProcess = topologicalSort(indicators);

      // If topological sort returns empty (shouldn't happen due to validation),
      // fall back to original order
      if (indicatorsToProcess.length === 0) {
        log("topological sort returned empty, using original order");
        indicatorsToProcess = indicators;
      } else if (indicatorsToProcess.length !== indicators.length) {
        // This could happen if some indicators were filtered out
        log(
          "topological sort returned %d indicators, expected %d",
          indicatorsToProcess.length,
          indicators.length,
        );
        // Use original order as fallback
        indicatorsToProcess = indicators;
      }
    } catch (error) {
      // If sorting fails, fall back to simple mode (original order)
      log("topological sort failed, falling back to simple mode: %o", error);
      indicatorsToProcess = indicators;
    }
  }

  // PHASE 3: Set indicators context for ID→renderKey resolution
  // This allows getSourcePathAccessor to resolve immutable IDs to current renderKeys
  setIndicatorsContext(indicators);

  try {
    // Process indicators in dependency order
    indicatorsToProcess.forEach((i) => {
      try {
        // Skip indicators without a type (pure visualization indicators like Volume Profile)
        // These compute directly from OHLCV data in their series component
        if (!i.type) {
          log(
            "skipping indicator without type (pure visualization): %s",
            i.renderKey || i.key,
          );
          return;
        }

        const indicator = getCalculatedIndicatorData(i);
        processedData = indicator(processedData);
        processedData = applyOffsetBars(processedData, i);
      } catch (error) {
        log(
          "indicator processing error for %s: %o",
          i.renderKey || i.key,
          error,
        );

        // PHASE 3: Enhanced error handling for series-as-source
        if (calculationEnabled && i.config?.sourcePath?.includes(".")) {
          log(
            "series-as-source calculation failed for %s with source %s",
            i.renderKey || i.key,
            i.config.sourcePath,
          );
        }

        // Continue with other indicators if one fails
        // This prevents one broken indicator from breaking the entire chart
      }
    });
  } finally {
    // PHASE 3: Always clear context after processing, even if errors occurred
    clearIndicatorsContext();
  }

  return processedData;
};
